import { inject, InjectionToken, Service } from '@angular/core';

/** Bootstrap-time environment values provided by each app shell. */
export const ENVIRONMENT_CONFIG = new InjectionToken<
  Partial<EnvironmentConfig>
>('ENVIRONMENT_CONFIG');

/**
 * Configuration interface for environment variables
 */
export type EnvironmentConfig = {
  apiUrl: string;
  appEnv: 'local' | 'dev' | 'prd';
  /**
   * True when the build is served against the in-browser MSW mock backend
   * rather than a real API. Surfaces provenance notices on data the user
   * might otherwise mistake for live figures. Defaults to `false`.
   */
  demoMode: boolean;
  googleMapsApiKey: string;
  /** Cloud-based map style ID for light theme (and fallback when dark ID is unset). */
  googleMapsMapId: string;
  /** Optional map ID for dark theme; when empty, the light `googleMapsMapId` is used. */
  googleMapsMapIdDark: string;
  /**
   * Demo-only allowlist of domain keys whose MSW handlers should register.
   *
   * Omit / `undefined` / `['*']` → every domain is mocked (full-mock demo).
   * A concrete list → only those domains are mocked; others hit the real API.
   * `[]` → no API handlers (everything live).
   *
   * Defaults to `undefined` (treated as full mock when {@link demoMode} is true).
   */
  mockedDomains?: readonly string[];
};

/**
 * Service that provides access to runtime environment configuration.
 * This service is used by API clients to get environment variables like API URLs.
 *
 * The environment configuration is provided at bootstrap via {@link ENVIRONMENT_CONFIG}
 * or updated at runtime via {@link EnvironmentService.setConfig}.
 *
 * @example
 * ```typescript
 * // In app.config.ts
 * { provide: ENVIRONMENT_CONFIG, useValue: environment },
 *
 * // In your API client
 * export class CompanyApiClient extends BaseApiClient<Company> {
 *   protected envService = inject(EnvironmentService);
 *   protected http = inject(HttpClient);
 *
 *   protected template = {};
 *
 *   get url(): string {
 *     return `${this.envService.getApiUrl()}/companies`;
 *   }
 * }
 * ```
 */
@Service()
export class EnvironmentService {
  private readonly bootstrapConfig =
    inject(ENVIRONMENT_CONFIG, { optional: true }) ?? {};

  private config: EnvironmentConfig = {
    apiUrl: '',
    appEnv: 'prd',
    demoMode: false,
    googleMapsApiKey: '',
    googleMapsMapId: '',
    googleMapsMapIdDark: '',
    ...this.bootstrapConfig,
  };

  /**
   * Gets the API base URL for backend calls
   */
  getApiUrl(): string {
    return this.config.apiUrl;
  }

  /**
   * Gets the current application environment
   */
  getAppEnv(): 'local' | 'dev' | 'prd' {
    return this.config.appEnv;
  }

  /**
   * Gets the Google Maps API key
   */
  getGoogleMapsApiKey(): string {
    return this.config.googleMapsApiKey;
  }

  /**
   * Gets the Google Maps map ID for the light theme.
   */
  getGoogleMapsMapId(): string {
    return this.config.googleMapsMapId;
  }

  /**
   * Resolves the map ID for the current UI theme.
   * Uses the dark map ID when dark mode is active and that value is set; otherwise the light map ID.
   */
  getGoogleMapsMapIdForTheme(isDark: boolean): string {
    if (isDark && this.config.googleMapsMapIdDark) {
      return this.config.googleMapsMapIdDark;
    }
    return this.config.googleMapsMapId;
  }

  /**
   * True when the app is running against the MSW mock backend, so every
   * figure on screen is illustrative rather than a live measurement.
   *
   * This is the **demo build** flag — it does not mean every API domain is
   * mocked. Prefer {@link isDomainMocked} for overlays that must only fire
   * when a specific domain's handlers are registered (hybrid demo).
   */
  isDemoMode(): boolean {
    return this.config.demoMode;
  }

  /**
   * True when the given domain key is served by MSW in the current demo
   * configuration.
   *
   * Returns `false` when {@link isDemoMode} is false. When demo mode is on:
   * - omitted / `undefined` / list containing `'*'` → every domain is mocked
   * - concrete allowlist → only listed domains are mocked
   * - empty list → nothing is mocked
   *
   * Use this (not bare {@link isDemoMode}) for demo-only write overlays
   * (government factory edit, AAE showcase pin, illustrative-data notices)
   * so hybrid demos do not apply mock behaviour to live staging data.
   *
   * @param domain - Key matching a `MOCK_DOMAIN_HANDLERS` entry
   *   (`auth`, `factory`, `dashboard`, `map`, …).
   */
  isDomainMocked(domain: string): boolean {
    if (!this.config.demoMode) {
      return false;
    }
    const allowlist = this.config.mockedDomains;
    if (allowlist === undefined || allowlist.includes('*')) {
      return true;
    }
    // Concrete hybrid allowlists never mock auth (same rule as
    // `resolveMockedDomainKeys` in the demo app shell).
    if (domain === 'auth') {
      return false;
    }
    return allowlist.includes(domain);
  }

  /**
   * Sets the environment configuration (usually called at bootstrap)
   */
  setConfig(config: Partial<EnvironmentConfig>): void {
    this.config = { ...this.config, ...config };
  }
}
